import { z } from 'zod';

import { exhaustiveStringTuple } from '@willow/types-iso';

import { Expression } from '../../Expressions/AbstractExpressionEvaluator';
import { WorkflowExpressionEvaluator as ExpressionEvaluator } from '../../Expressions/WorkflowExpressionEvaluator';
import { GenericStepDefinition } from './GenericStepDefinition';
import { PhoneCallStepDefinition } from './PhoneCallStepDefinition';
import { SendLetterStepDefinition } from './SendLetterStepDefinition';

export const StepDefinition = z
  .union([PhoneCallStepDefinition, SendLetterStepDefinition, GenericStepDefinition])
  .superRefine((step, ctx) => {
    if (
      ExpressionEvaluator.expressionIsAlwaysFalse(step.automaticallyActivate) &&
      ExpressionEvaluator.expressionIsAlwaysFalse(step.canManuallyActivate)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Step can never be activated (neither automaticallyActivate nor canManuallyActivate can ever be true)',
        path: ['automaticallyActivate'],
      });
    }

    if (
      ExpressionEvaluator.expressionIsAlwaysFalse(step.automaticallyComplete) &&
      ExpressionEvaluator.expressionIsAlwaysFalse(step.canManuallyComplete)
    ) {
      ctx.addIssue({
        code: 'custom',
        message: 'Step can never be completed (neither automaticallyComplete nor canManuallyComplete can ever be true)',
        path: ['automaticallyComplete'],
      });
    }
  });

const ApiStepDefinitionMask = {
  id: true,
  title: true,
  description: true,
  dataCollection: true,
  // type: false,
  // isVisible: false,
  // displayInfo: false,
  // due: false,
  // retry: false,
  // createTask: false,
  // assignment: false,
  // actionsToTake: false,
  // automaticallyActivate: false,
  // automaticallyComplete: false,
  // automaticallyFail: false,
  // automaticallySkip: false,
  // canManuallyActivate: false,
  // canManuallySkip: false,
  // canManuallyComplete: false,
  // canManuallyFail: false,
  // supportNotes: false,
} as const satisfies Partial<Record<keyof StepDefinition, true>>;

export const ApiStepDefinition = z.union([
  PhoneCallStepDefinition.pick(ApiStepDefinitionMask),
  SendLetterStepDefinition.pick(ApiStepDefinitionMask),
  GenericStepDefinition.pick(ApiStepDefinitionMask),
]);

export type StepDefinition = z.infer<typeof StepDefinition>;

export type StepType = StepDefinition['type'];

export type StepTypeMap = {
  [K in StepType]: Extract<StepDefinition, { type: K }>;
};

export type StepDefinitionExpressionKeys = keyof {
  [K in keyof StepDefinition as StepDefinition[K] extends Expression | undefined ? K : never]: undefined;
};

export const StepDefinitionExpressionKeys = exhaustiveStringTuple<StepDefinitionExpressionKeys>()(
  'automaticallySkip',
  'automaticallyActivate',
  'automaticallyComplete',
  'automaticallyFail',
  'canManuallySkip',
  'canManuallyActivate',
  'canManuallyComplete',
  'canManuallyFail',
  'canEditAfterCompletion',
  'isVisible',
);
